"use client";

import { useReducer } from "react";

type Action<T> =
  | { type: "add"; value: T }
  | { type: "remove"; value: T }
  | { type: "toggle"; value: T }
  | { type: "clear" };

function setReducer<T>(state: Set<T>, action: Action<T>): Set<T> {
  const newSet = new Set(state);

  switch (action.type) {
    case "add":
      newSet.add(action.value);
      return newSet;
    case "remove":
      newSet.delete(action.value);
      return newSet;
    case "toggle":
      newSet.has(action.value)
        ? newSet.delete(action.value)
        : newSet.add(action.value);
      return newSet;
    case "clear":
      return new Set();
    default:
      return state;
  }
}

/**
 * Custom hook for managing a set of values with common set operations.
 * @param initialValues Optional initial values for the set
 * @returns A set and functions to modify it
 */
export function useSet<T>(initialValues?: Iterable<T>) {
  const [values, dispatch] = useReducer(
    setReducer<T>,
    initialValues ? new Set(initialValues) : new Set<T>()
  );

  return {
    values,
    add: (value: T) => dispatch({ type: "add", value }),
    remove: (value: T) => dispatch({ type: "remove", value }),
    toggle: (value: T) => dispatch({ type: "toggle", value }),
    clear: () => dispatch({ type: "clear" }),
    has: (value: T) => values.has(value),
  };
}
